import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { X, Camera, Sparkles } from "lucide-react";
import { api, mediaUrl } from "@/lib/api";
import { fadeUp, staggerParent, viewportOnce } from "@/lib/animations";

const MediaPress = () => {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [active, setActive] = useState(null);

  useEffect(() => {
    api.get("/gallery")
      .then((r) => setItems(Array.isArray(r.data) ? r.data : []))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  useEffect(() => {
    if (!active) return;
    const onKey = (e) => { if (e.key === "Escape") setActive(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active]);

  return (
    <div data-testid="media-press-page" className="bg-brand-cream">
      <section className="relative overflow-hidden pb-14 pt-32 sm:pt-36 lg:pt-40">
        <div className="pointer-events-none absolute -left-40 top-10 h-[30rem] w-[30rem] rounded-full bg-brand-magenta/10 blur-[120px]" />
        <div className="pointer-events-none absolute -right-40 top-40 h-[30rem] w-[30rem] rounded-full bg-brand-blue/10 blur-[120px]" />
        <div className="relative mx-auto max-w-[1400px] px-5 sm:px-8">
          <motion.nav initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="mb-7 flex flex-wrap items-center gap-2 font-body text-sm text-brand-ink/50" data-testid="media-press-breadcrumb">
            <Link to="/" className="transition-colors duration-300 hover:text-brand-magenta">Home</Link>
            <span>/</span>
            <span className="text-brand-ink/80">Media &amp; Press</span>
          </motion.nav>

          <motion.div variants={staggerParent} initial="hidden" animate="show" className="max-w-3xl">
            <motion.p variants={fadeUp} className="mb-5 inline-flex items-center gap-2 rounded-full border border-brand-line bg-white/60 px-4 py-2 font-heading text-xs font-semibold uppercase tracking-[0.18em] text-brand-ink/70 backdrop-blur">
              <Sparkles className="h-3.5 w-3.5 text-brand-magenta" /> In the Spotlight
            </motion.p>
            <motion.h1 variants={fadeUp} className="font-heading text-[2.7rem] font-semibold leading-[0.98] tracking-tight text-brand-ink sm:text-6xl lg:text-[4.5rem]">
              Media &amp; <span className="brand-gradient-text">Press</span>
            </motion.h1>
            <motion.p variants={fadeUp} custom={2} className="mt-6 max-w-xl font-body text-base leading-relaxed text-brand-ink/70 sm:text-lg">
              Glimpses from our programs, events and newsroom mentions — moments of connection, energy and transformation captured over the years.
            </motion.p>
          </motion.div>
        </div>
      </section>

      <section className="pb-24 sm:pb-32" data-testid="gallery-section">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={viewportOnce} transition={{ duration: 0.6 }}
            className="mb-10 flex flex-col items-start justify-between gap-4 rounded-[24px] bg-brand-ink p-7 sm:flex-row sm:items-center sm:p-8" data-testid="social-gallery-cta">
            <div>
              <p className="font-heading text-xl font-semibold text-white">Social Media Gallery</p>
              <p className="mt-1 font-body text-sm text-white/60">Watch reels and videos from our sessions — right here, no redirects.</p>
            </div>
            <Link to="/social-gallery" data-testid="social-gallery-cta-button"
              className="group inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-brand-magenta px-6 py-3.5 font-body text-sm font-semibold text-white transition-transform duration-300 hover:scale-[1.04]">
              Watch Videos
              <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
            </Link>
          </motion.div>
          {loaded && items.length === 0 && (
            <div className="flex flex-col items-center rounded-[28px] border border-dashed border-brand-line bg-white/50 px-8 py-20 text-center" data-testid="gallery-empty">
              <Camera className="mb-4 h-8 w-8 text-brand-ink/30" />
              <p className="font-heading text-xl font-semibold text-brand-ink">Photos coming soon</p>
              <p className="mt-2 font-body text-sm text-brand-ink/55">We are curating our favourite moments — check back shortly.</p>
            </div>
          )}
          <div className="columns-1 gap-5 sm:columns-2 lg:columns-3">
            {items.map((g, i) => (
              <motion.button key={g.id} type="button" onClick={() => setActive(g)} data-testid={`gallery-item-${i}`}
                initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={viewportOnce} transition={{ duration: 0.6, delay: (i % 3) * 0.08 }}
                className="group relative mb-5 block w-full overflow-hidden rounded-[24px] border border-brand-line bg-white text-left">
                <img src={mediaUrl(g.url)} alt={g.caption || "Gallery photo"} loading="lazy"
                  className="w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]" />
                {g.caption && (
                  <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-brand-ink/80 to-transparent px-5 pb-4 pt-10 font-body text-sm font-medium text-white">
                    {g.caption}
                  </span>
                )}
              </motion.button>
            ))}
          </div>
        </div>
      </section>

      <AnimatePresence>
        {active && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[80] flex items-center justify-center bg-brand-ink/90 p-5 backdrop-blur-sm"
            onClick={() => setActive(null)} data-testid="gallery-lightbox">
            <button type="button" aria-label="Close" data-testid="gallery-lightbox-close"
              className="absolute right-5 top-5 flex h-11 w-11 items-center justify-center rounded-full border border-white/25 bg-white/10 text-white transition-colors duration-300 hover:bg-brand-magenta">
              <X className="h-5 w-5" />
            </button>
            <motion.figure initial={{ scale: 0.94, y: 16 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.96, y: 8 }} transition={{ duration: 0.3 }}
              className="max-h-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
              <img src={mediaUrl(active.url)} alt={active.caption || "Gallery photo"} className="max-h-[78vh] w-auto rounded-2xl object-contain" />
              {active.caption && <figcaption className="mt-4 text-center font-body text-sm text-white/80">{active.caption}</figcaption>}
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MediaPress;
